/* eslint-disable react/no-this-in-sfc */
/* eslint-disable func-names */
import { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { cancelOrder, editBracketOrder } from 'actions/trade';
import { ORDER_CATEGORY } from 'constants/enums';
import { addLogsToSentry } from 'helpers/sentry';
import { isMobileOrTablet } from 'helpers/utils';
import { allOpenOrdersSelector } from 'selectors/holdingsSelectors';
import { userSelector } from 'selectors/otherSelectors';

const OpenOrderLine = ({
  order,
  widgetRef,
  isNewTab,
  isChartReady,
  currentActiveTab,
  activeChartSymbol,
}) => {
  const orderLine = useRef(null);
  // const markPrice = markPriceSelectorById(order.product.id.toString());
  const [showOrderLine, setShowOrderLine] = useState(false);
  const previousTab = useRef('');

  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const color = order.side === 'sell' ? '#e55541' : '#28d591';
  const buttonCloseColor = '#153034';
  const buttonBorderColor = order.side === 'sell' ? '#e55541' : '#00ce7d';
  const orderInfo = JSON.stringify(order);

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      // eslint-disable-next-line no-underscore-dangle
      if (isChartReady && widgetRef?.current?._id && showOrderLine) {
        try {
          const chart = widgetRef.current.activeChart();
          if (!chart) throw new Error('Chart not available');
          const orderLineInstance = await chart.createOrderLine();

          orderLineInstance.onCancel(function () {
            this.setText('Cancelling...');
            dispatch(
              cancelOrder(
                {
                  id: order.id,
                  product_id: order.product.id,
                },
                {
                  chart: true,
                },
                { orderCategory: ORDER_CATEGORY.OPEN_ORDER }
              )
            );
          });

          orderLineInstance.onMove(function () {
            this.setText('Updating...');
            dispatch(
              editBracketOrder(
                {
                  id: order.id,
                  product_id: order.product.id,
                  limit_price: this.getPrice().toString(),
                },
                {
                  chart: true,
                }
              )
            ).catch(() => {
              if (orderLine.current) {
                orderLine.current.setPrice(order.stop_price);
                orderLine.current.setText(`${order.side} @ ${order.limit_price}`);
              }
            });
          });

          orderLineInstance.setModifyTooltip('Quantity');
          orderLineInstance.setText(`${order.side} @ ${order.limit_price}`);
          orderLineInstance.setLineColor(color);
          orderLineInstance.setBodyBorderColor(buttonBorderColor);
          orderLineInstance.setBodyBackgroundColor('#153034');
          orderLineInstance.setQuantityBackgroundColor(color);
          orderLineInstance.setQuantityBorderColor(color);
          orderLineInstance.setBodyTextColor('#ffffff');
          orderLineInstance.setBodyFont('12px Verdana');
          orderLineInstance.setQuantityFont('12px Verdana');
          orderLineInstance.setCancelButtonBorderColor(buttonBorderColor);
          orderLineInstance.setCancelButtonBackgroundColor(buttonCloseColor);
          orderLineInstance.setCancelButtonIconColor('#e55541');
          orderLineInstance.setPrice(order.limit_price);
          orderLineInstance.setQuantity(order.size);
          orderLineInstance.setExtendLeft(true);
          orderLineInstance.setLineLength(isNewTab ? 68 : 65);

          if (isMobileOrTablet()) {
            orderLineInstance.setBodyFont('10px Verdana');
            orderLineInstance.setQuantityFont('10px Verdana');
          }

          orderLine.current = orderLineInstance;
        } catch (e) {
          console.error('DEBUG: Error creating order line', e);
          addLogsToSentry(user, `Error creating order line ${e}`);
        }
      }
    }, 1000);
    return function cleanUp() {
      clearTimeout(timeoutId);
      if (orderLine.current) {
        // eslint-disable-next-line no-underscore-dangle
        if (orderLine.current._line) {
          orderLine.current.remove();
        }
        orderLine.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderInfo,
    widgetRef.current,
    isChartReady,
    currentActiveTab,
    showOrderLine,
    activeChartSymbol,
  ]);

  useEffect(() => {
    if (previousTab?.current !== currentActiveTab) {
      setShowOrderLine(false);
      setTimeout(() => {
        setShowOrderLine(true);
      }, 1000);
    }
  }, [currentActiveTab]);

  return null;
};

// @todo bala move it formula.js and add unit test for it
// const checkImmediateExecution = (side, value, markPrice) => {
//   ///const selectedProductIdSelector)
//   // for buy orders: if new limit price > mark price: show immediate liquidation message
//   // for sell orders: if new limit price < mark price: show immediate liquidation message
//   if (R.equals(side, 'sell')) {
//     return R.lte(Number(value), Number(markPrice));
//   } else {
//     return R.gte(Number(value), Number(markPrice));
//   }
// };

const OpenOrdersWidget = ({
  isNewTab,
  widgetRef,
  isChartReady,
  currentActiveTab,
  activeChartSymbol,
}) => {
  const orders = useSelector(allOpenOrdersSelector);
  const filteredOrders = orders.filter(order =>
    activeChartSymbol?.includes(order.product.symbol)
  );
  return filteredOrders.map(order => (
    <OpenOrderLine
      key={order.id}
      order={order}
      isNewTab={isNewTab}
      widgetRef={widgetRef}
      isChartReady={isChartReady}
      currentActiveTab={currentActiveTab}
      activeChartSymbol={activeChartSymbol}
    />
  ));
};

export default OpenOrdersWidget;
